import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { getInstallmentStatusChipColors, getInstallmentStatusLabel } from "../../agreements/constants/installmentStatusOptions";
import { getPaymentMethodLabel } from "../../creditRecovery/constants/paymentMethodOptions";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { globalMaskDocument } from "../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../shared/utils/formatScaleValue";
import { PAYMENT_METHOD_ICONS, PAYMENT_METHOD_SHORT_LABEL } from "../constants/paymentMethodIcons";
import { ICollectionControlRow } from "../hooks/useCollectionControlInstallments";
import { CollectionControlRowMenu } from "./CollectionControlRowMenu";

interface Props {
	rows: ICollectionControlRow[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	onChanged: () => void;
}

const TABLE_COLUMN_COUNT = 8;

export const TableCollectionControl = ({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange, onChanged }: Props) => {
	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}
		>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						{["Certificado", "Locatário", "Imobiliária", "Parcela", "Vencimento", "Status", "Repasse", "Ações"].map((header, i) => (
							<TableCell key={i} align={i <= 2 ? "left" : "center"}>
								<Typography variant="button">{header}</Typography>
							</TableCell>
						))}
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.length === 0 ? (
						<TableRow>
							<TableCell colSpan={TABLE_COLUMN_COUNT} align="center" sx={{ py: 6 }}>
								<Typography variant="body2" color="text.secondary">
									Nenhuma parcela encontrada para os filtros aplicados.
								</Typography>
							</TableCell>
						</TableRow>
					) : (
						rows.map((row) => {
							const statusColors = getInstallmentStatusChipColors(row.installment.status);
							const transferRegistration = row.installment.agencyTransferRegistration;
							const paymentIcon = PAYMENT_METHOD_ICONS[row.paymentMethod] ?? PAYMENT_METHOD_ICONS.boleto;
							const paymentShortLabel = PAYMENT_METHOD_SHORT_LABEL[row.paymentMethod] ?? PAYMENT_METHOD_SHORT_LABEL.boleto;

							return (
								<TableRow key={`${row.agreementId}-${row.installment.number}`} hover>
									<TableCell>
										<Typography variant="body2">{row.certificateNumber || "-"}</Typography>
									</TableCell>

									<TableCell>
										<HtmlTooltip
											title={
												<Box>
													<Typography variant="subtitle2" fontWeight="bold">
														{row.proposal.name}
													</Typography>
												</Box>
											}
											arrow
											placement="top"
										>
											<Box>
												<Typography
													variant="body2"
													sx={{ maxWidth: "22ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
												>
													{row.proposal.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{globalMaskDocument(row.proposal.document)}
												</Typography>
											</Box>
										</HtmlTooltip>
									</TableCell>

									<TableCell>
										<Typography variant="body2" sx={{ maxWidth: "20ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
											{row.client?.name || "-"}
										</Typography>
									</TableCell>

									<TableCell align="center">
										<Stack spacing={0.1} alignItems="center">
											<Typography variant="body2" fontWeight={600}>
												{formatScaleValueBR(String(row.installment.value))}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{row.installment.number} de {row.installmentsCount}
											</Typography>
										</Stack>
									</TableCell>

									<TableCell align="center">
										<Stack spacing={0.3} alignItems="center">
											<Typography variant="body2">{formatDateFnsRemoveTimezone(row.installment.dueDate) || "-"}</Typography>
											<HtmlTooltip title={`Pagamento: ${getPaymentMethodLabel(row.paymentMethod)}`} placement="top">
												<Stack direction="row" spacing={0.4} alignItems="center">
													{paymentIcon}
													<Typography variant="caption" color="text.secondary">
														{paymentShortLabel}
													</Typography>
												</Stack>
											</HtmlTooltip>
										</Stack>
									</TableCell>

									<TableCell align="center">
										<Stack spacing={0.3} alignItems="center">
											<Chip
												size="small"
												label={getInstallmentStatusLabel(row.installment.status)}
												sx={{ bgcolor: statusColors.bg, color: statusColors.color, fontWeight: 500 }}
											/>
											{row.installment.status === "paid" && row.installment.paidAt && (
												<Typography variant="caption" color="text.secondary">
													{formatDateFnsRemoveTimezone(row.installment.paidAt)}
												</Typography>
											)}
										</Stack>
									</TableCell>

									<TableCell align="center">
										{transferRegistration ? (
											<Stack spacing={0.1} alignItems="center">
												<Typography variant="body2" fontWeight={600}>
													{formatScaleValueBR(String(transferRegistration.value))}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{formatDateFnsRemoveTimezone(transferRegistration.transferDate) || "-"}
												</Typography>
											</Stack>
										) : (
											<Typography variant="body2" color="text.secondary" fontStyle="italic">
												Não informado
											</Typography>
										)}
									</TableCell>

									<TableCell align="center">
										<CollectionControlRowMenu row={row} onChanged={onChanged} />
									</TableCell>
								</TableRow>
							);
						})
					)}
				</TableBody>
			</Table>

			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer>
	);
};
